import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from '../contexts/TranslationContext.tsx';
import { useAuth } from '../contexts/AuthContext.tsx';
import { API_BASE } from '../utils/apiEndpoints';
import './CookieConsentBanner.css';

const CONSENT_KEY = 'cookie_consent_accepted';

export function CookieConsentBanner() {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const checkAndShow = async () => {
      const stored = localStorage.getItem(CONSENT_KEY);
      if (stored) {
        setVisible(false);
        if (isAuthenticated) {
          const choice = stored === 'true' ? 'accepted' : stored;
          if (choice === 'accepted' || choice === 'declined') {
            try {
              await fetch(`${API_BASE}/api/cookie-consent`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ choice })
              });
            } catch {
              /* ignore */
            }
          }
        }
        return;
      }
      if (isAuthenticated) {
        try {
          const res = await fetch(`${API_BASE}/api/cookie-consent`, { credentials: 'include' });
          if (res.ok) {
            const data = await res.json();
            if (data.cookie_consent) {
              localStorage.setItem(CONSENT_KEY, data.cookie_consent);
              setVisible(false);
              return;
            }
          }
        } catch {
          /* ignore */
        }
      }
      setVisible(true);
    };
    checkAndShow();
  }, [isAuthenticated]);

  const saveChoice = async (choice: 'accepted' | 'declined') => {
    localStorage.setItem(CONSENT_KEY, choice);
    setVisible(false);

    if (isAuthenticated) {
      try {
        await fetch(`${API_BASE}/api/cookie-consent`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ choice })
        });
      } catch (err) {
        console.error('Failed to save cookie consent to server:', err);
      }
    }
  };

  const handleAccept = () => saveChoice('accepted');
  const handleDecline = () => saveChoice('declined');

  if (!visible) return null;

  return (
    <div className="cookie-consent-banner" role="dialog" aria-label={t('cookieConsent.ariaLabel')}>
      <div className="cookie-consent-content">
        <p className="cookie-consent-text">
          {t('cookieConsent.message')}{' '}
          <Link to="/cookies-policy" className="cookie-consent-link">
            {t('cookieConsent.policyLink')}
          </Link>
        </p>
        <div className="cookie-consent-buttons">
          <button
            type="button"
            className="cookie-consent-decline"
            onClick={handleDecline}
            aria-label={t('cookieConsent.decline')}
          >
            {t('cookieConsent.decline')}
          </button>
          <button
            type="button"
            className="cookie-consent-accept"
            onClick={handleAccept}
            aria-label={t('cookieConsent.accept')}
          >
            {t('cookieConsent.accept')}
          </button>
        </div>
      </div>
    </div>
  );
}
